import React, { useContext, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import classnames from 'classnames/bind';
import { InlineNotification, Input, Modal, Rating, Textarea, Typography } from 'gd-design-library';
import { Button } from '@components';
import { ProductService } from '@services';
import { useForm, useServiceRef } from 'hooks';
import { ROUTES } from 'constants/routes';
import { UserContext } from 'utils/UserContext';
import { formFields } from './form';
import styles from './WriteReviewModal.module.scss';

const cx = classnames.bind(styles);
const CN = 'write-review-modal';

interface WriteReviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  productId: string;
  onReviewSubmitted?: () => void;
}

const SESSION_KEY_PREFIX = 'reviewedProduct_';

export const WriteReviewModal: React.FC<WriteReviewModalProps> = ({ isOpen, onClose, productId, onReviewSubmitted }) => {
  const { user, isUserLoading } = useContext(UserContext);
  const router = useRouter();
  const [ rating, setRating ] = useState(0);
  const [ isSubmitting, setIsSubmitting ] = useState(false);
  const [ isSuccess, setIsSuccess ] = useState(false);
  const [ submitError, setSubmitError ] = useState('');
  const [ alreadyReviewed, setAlreadyReviewed ] = useState(false);
  const [ submitted, setSubmitted ] = useState(false);

  const productServiceRef = useServiceRef(ProductService);

  const {
    state,
    errors,
    handleBlur,
    handleFocus,
    handleAutofill,
    setFieldValues,
    getFormValidState,
    resetForm
  } = useForm(formFields);

  useEffect(() => {
    if (isOpen) {
      const reviewed = sessionStorage.getItem(`${SESSION_KEY_PREFIX}${productId}`);
      setAlreadyReviewed(!!reviewed);
      setIsSuccess(false);
      setSubmitError('');
      setRating(0);
      setSubmitted(false);
      resetForm();
    }
  }, [ isOpen, productId ]);

  const handleCommentChange = (e: React.ChangeEvent<HTMLTextAreaElement>): void => {
    setFieldValues({ comment: e.target.value });
  };

  const handleCommentBlur = (e: React.FocusEvent<HTMLTextAreaElement>): void => {
    handleBlur(e as unknown as React.FocusEvent<HTMLInputElement>);
  };

  const isSubmitDisabled = rating === 0 || isSubmitting;

  const handleSubmit = async (): Promise<void> => {
    const formValid = getFormValidState();
    setSubmitted(true);
    if (!formValid || rating === 0) return;

    setIsSubmitting(true);
    setSubmitError('');

    const success = await productServiceRef.current.submitReview(productId, {
      rating,
      title: state.title,
      comment: state.comment
    });

    setIsSubmitting(false);

    if (success) {
      sessionStorage.setItem(`${SESSION_KEY_PREFIX}${productId}`, '1');
      setIsSuccess(true);
      onReviewSubmitted?.();
    } else {
      setSubmitError('Something went wrong. Please try again.');
    }
  };

  const renderContent = (): React.ReactNode => {
    if (!isUserLoading && !user) {
      return (
        <div className={ cx(`${CN}__not-logged-in`) }>
          <InlineNotification variant='info'>
            <Typography>Please log in to write a review.</Typography>
          </InlineNotification>
          <div className={ cx(`${CN}__actions`) }>
            <Button onClick={ () => router.push({ pathname: ROUTES.signIn, query: { returnUrl: router.asPath } }) } className={ cx(`${CN}__login-button`) }>
              Log In
            </Button>
            <Button variant='outlined' onClick={ onClose } className={ cx(`${CN}__close-button`) }>
              Close
            </Button>
          </div>
        </div>
      );
    }

    if (alreadyReviewed) {
      return (
        <div className={ cx(`${CN}__already-reviewed`) }>
          <InlineNotification variant='info'>
            <Typography>You have already submitted a review for this product in this session.</Typography>
          </InlineNotification>
          <Button variant='outlined' onClick={ onClose } className={ cx(`${CN}__close-button`) }>
            Close
          </Button>
        </div>
      );
    }

    if (isSuccess) {
      return (
        <div className={ cx(`${CN}__success`) }>
          <InlineNotification variant='success'>
            <Typography>Thank you for your review!</Typography>
          </InlineNotification>
          <Button variant='outlined' onClick={ onClose } className={ cx(`${CN}__close-button`) }>
            Close
          </Button>
        </div>
      );
    }

    return (
      <form className={ cx(`${CN}__form`) }>
        <Typography variant='strong'>Overall Rating *</Typography>
        <div className={ cx(`${CN}__rating-section`) }>
          <Rating value={ rating } onChange={ value => setRating(value) } />
        </div>
        <Typography variant='strong'>Review</Typography>
        <div className={ cx(`${CN}__fields-row`) }>
          <Input
            required
            name='title'
            placeholder='Review title *'
            value={ state.title }
            onChange={ e => setFieldValues({ title: e.target.value }) }
            onBlur={ handleBlur }
            onFocus={ handleFocus }
            onAnimationStart={ handleAutofill }
            maxLength={ 100 }
            color={ submitted && !errors.title.isValid ? 'error' : 'primary' }
            helperText={ submitted && !errors.title.isValid ? errors.title.message : '' }
            className={ cx(`${CN}__field`) }
          />
        </div>
        <div className={ cx(`${CN}__textarea-wrapper`) }>
          <Textarea
            name='comment'
            placeholder='Review *'
            value={ state.comment }
            onChange={ handleCommentChange }
            onBlur={ handleCommentBlur }
            maxLength={ 2000 }
            rows={ 4 }
            className={ cx(`${CN}__textarea`, { [`${CN}__textarea--error`]: submitted && !errors.comment.isValid }) }
          />
          {submitted && !errors.comment.isValid && (
            <Typography variant='caption' className={ cx(`${CN}__error`) }>
              {errors.comment.message}
            </Typography>
          )}
        </div>
        {submitError && (
          <InlineNotification variant='error'>
            <Typography variant='caption'>{submitError}</Typography>
          </InlineNotification>
        )}
        <Button
          onClick={ handleSubmit }
          disabled={ isSubmitDisabled }
          loading={ isSubmitting }
          className={ cx(`${CN}__submit-button`) }
        >
          Submit
        </Button>
      </form>
    );
  };

  return (
    <Modal isOpen={ isOpen } onClose={ onClose } title='Write a Review'>
      <div className={ cx(CN) }>
        {renderContent()}
      </div>
    </Modal>
  );
};
